/**
 * Turning a resolved address into the location the app reads.
 *
 * `/app` and `/app/contaminants` read `?lat&lng&loc`, which is
 * what makes a result shareable and lets the map hand state back without a
 * re-search. Every surface that can start a search — the zero state and the
 * field the marketing pages embed — has to produce that query identically, so
 * the encoding lives here rather than in each of them.
 *
 * Accepts either a Places `PlaceResult` or a `GeocoderResult`: both carry
 * `geometry.location` as a `LatLng`, and both are things a visitor can hand us
 * (a picked suggestion, or typed text we geocoded ourselves).
 */
export const placeToParams = (place) => {
  const location = place?.geometry?.location;
  if (!location) return null;

  const lat = typeof location.lat === "function" ? location.lat() : location.lat;
  const lng = typeof location.lng === "function" ? location.lng() : location.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    // Encoded here because every reader decodes it — `searchParams.get("loc")`
    // is passed through `decodeURIComponent` on all three surfaces.
    loc: encodeURIComponent(place.formatted_address || place.name || ""),
  });
};

/**
 * Whether a query carries a usable location — the same test the result pages
 * apply before fetching. `/app` uses it to choose between the console and the
 * entry screen.
 */
export const hasLocationParams = (searchParams) =>
  Number.isFinite(parseFloat(searchParams?.get?.("lat"))) &&
  Number.isFinite(parseFloat(searchParams?.get?.("lng")));

export default placeToParams;
